import { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { BsFileEarmarkSpreadsheet, BsBoxArrowInDown } from 'react-icons/bs';

import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import CrmImportWizard from './CrmImportWizard';
import PipedriveImportWizard from './PipedriveImportWizard';

/**
 * Entry point for CRM imports: lets the user pick the source, then opens the
 * matching wizard. `isOpen` controls the source-picker; each wizard manages its
 * own steps.
 */
export default function ImportLauncher({ isOpen, onClose }) {
	const [mode, setMode] = useState(null); // 'simple' | 'pipedrive' | null

	const handleClose = useCallback(() => {
		setMode(null);
		onClose();
	}, [onClose]);

	if (mode === 'simple') {
		return <CrmImportWizard isOpen onClose={handleClose} />;
	}
	if (mode === 'pipedrive') {
		return <PipedriveImportWizard isOpen onClose={handleClose} />;
	}

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="l">
			<Panel.Header onClose={handleClose}>Import-Quelle wählen</Panel.Header>
			<Panel.Body>
				<div className="grid grid-cols-2 gap-4">
					<SourceCard
						icon={BsFileEarmarkSpreadsheet}
						title="CSV-Datei"
						description="Kontakte + Firmen aus einer CSV-Datei (eine Zeile = ein Kontakt + Firma)."
						onClick={() => setMode('simple')}
					/>
					<SourceCard
						icon={BsBoxArrowInDown}
						title="Aus Pipedrive migrieren"
						description="Alle Pipedrive-Exporte: Organisationen, Personen, Deals, Aktivitäten und Notizen."
						onClick={() => setMode('pipedrive')}
					/>
				</div>
			</Panel.Body>
		</Modal>
	);
}

function SourceCard({ icon: Icon, title, description, onClick }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex flex-col items-start p-5 border border-bgBase-500 rounded-card text-left hover:border-primary-400 hover:bg-primary-100 transition-colors"
		>
			<Icon className="w-7 h-7 text-primary-500 mb-3" />
			<span className="text-sm font-semibold text-text">{title}</span>
			<span className="text-xs text-bgBase-800 mt-1">{description}</span>
		</button>
	);
}

ImportLauncher.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

SourceCard.propTypes = {
	icon: PropTypes.elementType.isRequired,
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	onClick: PropTypes.func.isRequired,
};
